'use client';

import { Fragment, type ReactNode } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { Iconify } from '@/components/common/iconify';
import {
  ACCESS_LEVELS,
  ACCOUNT_SETTINGS,
  ADD_ONS,
  AGENCY_MARKUP_FAQ,
  AGENCY_MARKUP_FLOW,
  AGENCY_MARKUP_WHERE,
  BILLING_RULES,
  FAMILIES,
  ITEM_DESCRIPTIONS,
  ORG_CONCEPTS,
  ORG_FAQ,
  PAYMENT_METHOD_POINTS,
  PIXEL_FAQ,
  PIXEL_FLOW,
  PIXEL_PLATFORM_GUIDES,
  PIXEL_TROUBLESHOOTING,
  PIXEL_USES,
  PLAN_TIERS,
  SCOPE_SWITCH_POINTS,
  SUBSCRIBE_FLOW,
  SUBSCRIPTION_FAQ,
  SUB_TENANT_TYPES,
  TEAM_FAQ,
  TEAM_FLOW,
  WALLET_FAQ,
  WALLET_FLOW,
  WALLET_SPEND_CATEGORIES,
  WORKFLOW,
  type Faq,
  type Point,
} from './content';
import type { ModuleGroup } from './steps';

// Full-text search across everything the tour can show. Entries carry the step they live
// on and the exact title rendered there, so a result can jump to the step and highlight it.

export type SearchKind = 'FAQ' | 'Topic' | 'Tool' | 'Guide' | 'Step';

export type SearchEntry = {
  stepId: string;
  kind: SearchKind;
  /** Rendered title of the target element (matches its data-search-title). */
  title: string;
  text: string;
};

type BuildArgs = {
  /** Steps actually present in the tour, id → sidebar label. */
  steps: Array<{ id: string; label: string }>;
  modules: ModuleGroup[];
  isOwner: boolean;
  showBlyn: boolean;
};

const points = (stepId: string, list: Point[], kind: SearchKind = 'Topic'): SearchEntry[] =>
  list.map((p) => ({ stepId, kind, title: p.title, text: p.text }));

const faqs = (stepId: string, list: Faq[]): SearchEntry[] =>
  list.map((f) => ({ stepId, kind: 'FAQ', title: f.q, text: f.a }));

export function buildSearchIndex({ steps, modules, isOwner, showBlyn }: BuildArgs): SearchEntry[] {
  const present = new Set(steps.map((s) => s.id));
  const noBlyn = <T extends { title: string }>(list: T[]) => (showBlyn ? list : list.filter((x) => !/blyn/i.test(x.title)));

  const entries: SearchEntry[] = [
    ...steps.map((s) => ({ stepId: s.id, kind: 'Step' as const, title: s.label, text: '' })),

    ...points('organization', ORG_CONCEPTS),
    ...points('organization', SUB_TENANT_TYPES),
    ...points('organization', SCOPE_SWITCH_POINTS),
    ...faqs('organization', ORG_FAQ),
    ...(isOwner
      ? [
          { stepId: 'organization', kind: 'Topic' as const, title: 'Agency markup', text: 'Add your agency fee on top of campaign costs. The client sees base cost plus markup, e.g. $100 + 30% = $130.' },
          ...points('organization', AGENCY_MARKUP_FLOW),
          ...points('organization', AGENCY_MARKUP_WHERE),
          ...faqs('organization', AGENCY_MARKUP_FAQ),
        ]
      : []),

    ...points('team', TEAM_FLOW),
    ...ACCESS_LEVELS.map((l) => ({ stepId: 'team', kind: 'Topic' as const, title: l.level, text: `${l.who}. ${l.can}` })),
    ...faqs('team', TEAM_FAQ),

    ...points('subscription', SUBSCRIBE_FLOW, 'Guide'),
    ...points('subscription', PAYMENT_METHOD_POINTS),
    ...points('subscription', BILLING_RULES),
    ...points('subscription', noBlyn(ADD_ONS)),
    ...PLAN_TIERS.map((t) => ({ stepId: 'subscription', kind: 'Topic' as const, title: `${t.name} plan`, text: t.text })),
    ...faqs('subscription', SUBSCRIPTION_FAQ),

    ...points('wallet', WALLET_FLOW),
    { stepId: 'wallet', kind: 'Topic', title: 'What the wallet pays for', text: WALLET_SPEND_CATEGORIES.join(', ') },
    ...faqs('wallet', WALLET_FAQ),

    ...points('pixel', PIXEL_FLOW, 'Guide'),
    ...PIXEL_PLATFORM_GUIDES.map((g) => ({
      stepId: 'pixel',
      kind: 'Guide' as const,
      title: g.label,
      text: `Install the tracking pixel on ${g.label}. ${g.steps.join(' ')} ${g.note ?? ''}`,
    })),
    ...points('pixel', PIXEL_TROUBLESHOOTING),
    ...points('pixel', PIXEL_USES),
    ...faqs('pixel', PIXEL_FAQ),

    ...WORKFLOW.map((w) => ({ stepId: 'workflow', kind: 'Topic' as const, title: w.title, text: w.text })),

    ...ACCOUNT_SETTINGS.map((a) => ({ stepId: 'settings', kind: 'Topic' as const, title: a.title, text: a.text })),
  ];

  for (const m of modules) {
    const fam = FAMILIES[m.key];
    const stepId = `module-${m.key}`;
    entries.push(
      { stepId, kind: 'Topic', title: fam.label, text: `${fam.tagline}. ${fam.overview}` },
      ...points(stepId, fam.capabilities),
      ...points(stepId, fam.getStarted, 'Guide'),
      ...faqs(stepId, fam.faq)
    );
    for (const item of m.items) {
      for (const leaf of item.children?.length ? item.children : [item]) {
        entries.push({ stepId, kind: 'Tool', title: leaf.title, text: ITEM_DESCRIPTIONS[leaf.path] ?? '' });
      }
    }
  }

  return entries.filter((e) => present.has(e.stepId));
}

const tokenize = (q: string) =>
  q
    .toLowerCase()
    .split(/\s+/)
    .map((t) => t.replace(/[^a-z0-9$%.-]/g, ''))
    .filter((t) => t.length > 0);

/** Every query word must appear in the title or text; title hits and FAQ/step kinds rank higher. */
export function searchIndex(index: SearchEntry[], query: string, limit = 40): SearchEntry[] {
  const tokens = tokenize(query);
  if (!tokens.length) return [];
  const scored: Array<{ e: SearchEntry; score: number }> = [];
  for (const e of index) {
    const title = e.title.toLowerCase();
    const hay = `${title} ${e.text.toLowerCase()}`;
    if (!tokens.every((t) => hay.includes(t))) continue;
    let score = 0;
    for (const t of tokens) {
      if (title.startsWith(t)) score += 6;
      else if (title.includes(t)) score += 4;
      else score += 1;
    }
    if (e.kind === 'Step') score += 3;
    if (e.kind === 'FAQ' && tokens.length > 1) score += 1;
    scored.push({ e, score });
  }
  return scored.sort((a, b) => b.score - a.score).slice(0, limit).map((s) => s.e);
}

// ----------------------------------------------------------------------

function Highlight({ text, tokens }: { text: string; tokens: string[] }) {
  const theme = useTheme();
  if (!tokens.length || !text) return <>{text}</>;
  const escaped = tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const parts = text.split(new RegExp(`(${escaped.join('|')})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        tokens.some((t) => part.toLowerCase() === t) ? (
          <Box
            key={i}
            component="mark"
            sx={{ bgcolor: alpha(theme.palette.primary.main, 0.25), color: 'inherit', borderRadius: 0.5, px: 0.25 }}
          >
            {part}
          </Box>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        )
      )}
    </>
  );
}

/** Short window of `text` around the first matching word. */
function snippet(text: string, tokens: string[], radius = 90): string {
  if (!text) return '';
  const lower = text.toLowerCase();
  const hit = tokens.map((t) => lower.indexOf(t)).filter((i) => i >= 0).sort((a, b) => a - b)[0] ?? 0;
  const start = Math.max(0, hit - radius);
  const end = Math.min(text.length, hit + radius * 1.5);
  return `${start > 0 ? '…' : ''}${text.slice(start, end).trim()}${end < text.length ? '…' : ''}`;
}

const KIND_ICON: Record<SearchKind, string> = {
  Step: 'solar:bookmark-linear',
  FAQ: 'solar:question-circle-linear',
  Topic: 'solar:document-text-linear',
  Tool: 'solar:widget-5-linear',
  Guide: 'solar:list-check-linear',
};

export const SEARCH_SUGGESTIONS = ['add card', 'free trial', 'invite', 'auto-recharge', 'pixel', 'sub-tenant', 'invoices', 'journeys'];

export function SearchResults({
  query,
  results,
  stepLabel,
  onPick,
  onSuggest,
  footer,
}: {
  query: string;
  results: SearchEntry[];
  stepLabel: (stepId: string) => string;
  onPick: (e: SearchEntry) => void;
  onSuggest: (q: string) => void;
  footer?: ReactNode;
}) {
  const theme = useTheme();
  const tokens = tokenize(query);

  return (
    <Box>
      <Typography variant="overline" color="primary.main">
        Search
      </Typography>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2.5 }}>
        {results.length ? `${results.length} result${results.length === 1 ? '' : 's'} for “${query.trim()}”` : `No results for “${query.trim()}”`}
      </Typography>

      {results.length === 0 ? (
        <Box sx={{ p: 3, borderRadius: 2, border: 1, borderColor: 'divider', textAlign: 'center' }}>
          <Iconify icon="solar:magnifer-linear" width={32} sx={{ color: 'primary.main', mb: 1 }} />
          <Typography variant="body2" color="text.primary" sx={{ mb: 2 }}>
            Try different words, or one of these:
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75} justifyContent="center">
            {SEARCH_SUGGESTIONS.map((s) => (
              <Chip key={s} label={s} size="small" variant="outlined" onClick={() => onSuggest(s)} />
            ))}
          </Stack>
        </Box>
      ) : (
        <Stack spacing={1}>
          {results.map((r, i) => (
            <Stack
              key={`${r.stepId}-${r.kind}-${r.title}-${i}`}
              direction="row"
              spacing={1.5}
              role="button"
              tabIndex={0}
              onClick={() => onPick(r)}
              onKeyDown={(e) => e.key === 'Enter' && onPick(r)}
              sx={{
                p: 1.75,
                borderRadius: 1.5,
                border: 1,
                borderColor: 'divider',
                cursor: 'pointer',
                '&:hover, &:focus-visible': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.04), outline: 'none' },
              }}
            >
              <Iconify icon={KIND_ICON[r.kind]} width={20} sx={{ color: 'primary.main', flexShrink: 0, mt: 0.25 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.25 }}>
                  <Typography variant="subtitle2">
                    <Highlight text={r.title} tokens={tokens} />
                  </Typography>
                  <Chip label={r.kind === 'Step' ? 'Step' : stepLabel(r.stepId)} size="small" sx={{ height: 20, fontSize: 11 }} />
                </Stack>
                {r.text && (
                  <Typography variant="body2" color="text.primary">
                    <Highlight text={snippet(r.text, tokens)} tokens={tokens} />
                  </Typography>
                )}
              </Box>
              <Iconify icon="solar:arrow-right-linear" width={18} sx={{ color: 'primary.main', flexShrink: 0, mt: 0.25 }} />
            </Stack>
          ))}
        </Stack>
      )}
      {footer}
    </Box>
  );
}
